import { useId, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { QuoteRow } from "@/hooks/useQuotes";
import { cn } from "@/lib/utils";
import { formatMoney, paymentPresets, pendingEtransferSupported, type PaymentSummary } from "./drawerLogic";
import { useSquarePaymentLink, useWaitOnEtransfer } from "./usePaymentActions";

interface CollectPaymentPanelProps {
  quote: QuoteRow;
  summary: PaymentSummary;
  depositPct: number;
  /** app_settings' e-transfer email; Waiting on e-transfer needs it (the old dialog's guard). */
  etransferEmail: string | null;
  onClose: () => void;
}

/** Why Waiting on e-transfer is off, or null when it can be used. */
function waitBlocked(quote: QuoteRow, summary: PaymentSummary, etransferEmail: string | null): string | null {
  if (!pendingEtransferSupported(quote)) return "Available once this update is live";
  if (summary.pendingAmount > 0) return `Already waiting on ${formatMoney(summary.pendingAmount)}. Mark it received first.`;
  if (!etransferEmail) return "Set the e-transfer email in Settings first";
  return null;
}

/** Collect payment: presets, an amount, a Square link or a pending e-transfer (#1559). */
export function CollectPaymentPanel({ quote, summary, depositPct, etransferEmail, onClose }: CollectPaymentPanelProps) {
  const total = Number(quote.total) || 0;
  const presets = paymentPresets({ total, paid: summary.paid, depositPct });
  const [preset, setPreset] = useState<number | null>(presets.length ? 0 : null);
  const [amountText, setAmountText] = useState((presets[0]?.[1] ?? summary.balance).toFixed(2));
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const link = useSquarePaymentLink(quote);
  const etransfer = useWaitOnEtransfer(quote);
  const id = useId();

  const amount = parseFloat(amountText) || 0;
  const valid = amount > 0 && amount <= summary.balance + 0.005;
  const blocked = waitBlocked(quote, summary, etransferEmail);

  const sendLink = async () => {
    const result = await link.send(amount);
    if (!result) return;
    if (result.copied) onClose();
    else setManualUrl(result.url);
  };
  const copyAgain = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Square link copied.");
    } catch {
      toast.error(`Select and copy: ${url}`);
    }
  };

  return (
    <div className="grid gap-2.5 rounded-lg bg-background p-2.5">
      <h4 className="text-[13.5px] font-semibold">Collect payment</h4>
      <div role="group" aria-label="Amount presets" className="flex flex-wrap gap-1.5">
        {presets.map(([label, value], i) => (
          <button
            key={label}
            type="button"
            aria-pressed={preset === i}
            onClick={() => {
              setPreset(i);
              setAmountText(value.toFixed(2));
            }}
            className={cn(
              "rounded-full border bg-card px-[11px] py-[3px] text-[12.5px] font-semibold",
              preset === i && "border-primary bg-status-ok-soft text-status-ok",
            )}
          >
            {label} · {formatMoney(value)}
          </button>
        ))}
      </div>
      <div className="grid max-w-[180px] gap-[3px] text-[12px] text-muted-foreground">
        <label htmlFor={`${id}-amount`}>Amount</label>
        <Input
          id={`${id}-amount`}
          inputMode="decimal"
          value={amountText}
          onChange={(e) => {
            setAmountText(e.target.value);
            setPreset(null);
          }}
          className="h-8 text-right tabular-nums text-foreground"
        />
      </div>
      {manualUrl && (
        <div className="grid gap-1.5 rounded-lg bg-status-warn-soft px-2.5 py-2 text-[13px] text-status-warn">
          <span className="font-semibold">Couldn't copy the link. Select and copy it:</span>
          <div className="flex gap-2">
            <Input readOnly aria-label="Square payment link" value={manualUrl} onFocus={(e) => e.target.select()} className="h-8 bg-card text-foreground" />
            <Button type="button" variant="outline" size="sm" className="h-8 bg-card" onClick={() => copyAgain(manualUrl)}>Copy</Button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" className="h-8 bg-card" onClick={onClose}>Cancel</Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 bg-card"
          title={blocked ?? undefined}
          disabled={!valid || !!blocked || etransfer.waiting}
          onClick={async () => {
            if (await etransfer.wait(amount)) onClose();
          }}
        >
          Waiting on e-transfer
        </Button>
        <Button type="button" size="sm" className="h-8" disabled={!valid || link.sending} onClick={sendLink}>
          Send Square link
        </Button>
      </div>
      {blocked && <p className="text-[12px] text-muted-foreground">{blocked}</p>}
    </div>
  );
}
